/**
 * The Web Share API wrapper.
 *
 * One rule dominates this file: `navigator.share()` requires transient user
 * activation. Awaiting anything - the capture, a decode, a fetch - before
 * calling it burns the activation and iOS rejects with `NotAllowedError`. That
 * is the entire reason the UI shows a preview instead of sharing straight from
 * the shutter: by the time the share button exists, the `File` is already built,
 * so its click handler can call `sharePhoto` with nothing awaited in front.
 *
 * See https://webkit.org/blog/13862/the-user-activation-api/ - there is no code
 * trick that restores activation, only a second, deliberate tap.
 */

export type ShareOutcome =
  /** The share sheet opened and the user completed it. */
  | 'shared'
  /** The share sheet opened and the user dismissed it. Not an error. */
  | 'cancelled'
  /** No Web Share API, or this file type is not shareable here. */
  | 'unsupported'

/**
 * Synchronous, so the UI can decide between a share button and the long-press
 * hint before showing the preview.
 *
 * `canShare` is feature-detected separately from `share`: desktop Chrome and
 * some Android WebViews expose `share` for text only, and calling it with files
 * there throws instead of degrading.
 */
export const canSharePhoto = (file: File): boolean =>
  typeof navigator.canShare === 'function' &&
  typeof navigator.share === 'function' &&
  navigator.canShare({files: [file]})

/**
 * MUST be called synchronously from a click handler.
 *
 * Only `files` is passed. iOS has a history of rejecting a share whose payload
 * mixes files with `text`/`url`, and this campaign has no caption to attach
 * anyway - the photo carries no burnt-in branding, so there is nothing for a
 * caption to be consistent with.
 */
export const sharePhoto = async (file: File): Promise<ShareOutcome> => {
  if (!canSharePhoto(file)) return 'unsupported'
  try {
    await navigator.share({files: [file]})
    return 'shared'
  } catch (error: unknown) {
    // Dismissing the share sheet rejects. Surfacing that as a failure would tell
    // the user something broke when they are the one who cancelled.
    if (error instanceof DOMException && error.name === 'AbortError') return 'cancelled'
    throw error
  }
}
